import React, { createContext, useContext, useMemo, useState, useCallback, useRef } from "react";

const ConversationCtx = createContext(null);

export function ConversationProvider({ onPlayTick, children }) {
  const [near, setNear] = useState(null);
  const [bubble, setBubble] = useState(null);
  const [activeId, setActiveId] = useState(null);

  // nearest-candidate tracking (per frame-ish)
  const candidatesRef = useRef(new Map()); // id -> { id, prompt, text, d, ts }

  const clearNear = useCallback(() => {
    setNear(null);
  }, []);

  // ✅ NEW: register this NPC as a "near" candidate with distance
  const registerNear = useCallback((payload) => {
    // payload: { id, prompt, text, d }
    if (!payload?.id) return;

    const now = performance.now();
    candidatesRef.current.set(payload.id, { ...payload, ts: now });

    // choose closest candidate (ignore stale ones)
    let best = null;
    for (const c of candidatesRef.current.values()) {
      // drop stale candidates (NPC didn't report recently)
      if (now - c.ts > 250) continue;

      if (!best || c.d < best.d) best = c;
    }

    // update near only if it actually changed
    setNear((prev) => {
      const prevId = prev?.id ?? null;
      const nextId = best?.id ?? null;
      if (prevId === nextId && prev?.prompt === best?.prompt && prev?.text === best?.text) return prev;
      return best ? { id: best.id, prompt: best.prompt, text: best.text } : null;
    });
  }, []);

  // ✅ NEW: unregister candidate when leaving radius
  const unregisterNear = useCallback((id) => {
    if (!id) return;
    candidatesRef.current.delete(id);

    const now = performance.now();
    let best = null;
    for (const c of candidatesRef.current.values()) {
      if (now - c.ts > 250) continue;
      if (!best || c.d < best.d) best = c;
    }

    setNear(best ? { id: best.id, prompt: best.prompt, text: best.text } : null);
  }, []);

  const canInteract = useCallback((id) => {
    if (!id) return false;
    if (!near) return false;
    return near.id === id;
  }, [near]);

  const beginTalk = useCallback((id) => {
    if (!id) return;
    setActiveId(id);
  }, []);

  const endTalk = useCallback((id) => {
    setActiveId((cur) => (cur === id ? null : cur));
    setBubble((b) => (b?.id === id ? null : b));
  }, []);

  const interact = useCallback((id, text) => {
    if (!id) return;

    setActiveId(id);
    setNear((n) => (n?.id === id ? null : n));
    setBubble({ id, text });

    onPlayTick?.();

    window.setTimeout(() => {
      setBubble((b) => (b?.id === id ? null : b));
    }, 1800);
  }, [onPlayTick]);

  const value = useMemo(() => ({
    near,
    bubble,
    activeId,
    registerNear,
    unregisterNear,
    clearNear,
    interact,
    canInteract,
    beginTalk,
    endTalk,
  }), [near, bubble, activeId, registerNear, unregisterNear, clearNear, interact, canInteract, beginTalk, endTalk]);

  return <ConversationCtx.Provider value={value}>{children}</ConversationCtx.Provider>;
}

export function useConversation() {
  const ctx = useContext(ConversationCtx);
  if (!ctx) throw new Error("useConversation must be used inside <ConversationProvider />");
  return ctx;
}
